import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, CheckCircle, DeleteOutline, OpenInNew } from "@mui/icons-material";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import EvaluationDetailDialog from "../evaluations/EvaluationDetailDialog";

// 報告対象の項目（アプリ側 evaluationReportService の定義と対応。結果画面のセクション名）
const CATEGORY_LABELS: Record<string, string> = {
  detailScores: "詳細スコア",
  shortReview: "短評",
  strengths: "良い点",
  improvements: "改善ポイント",
  scoredChecklists: "採点の内訳",
  improvedExample: "全部反映するとこうなる",
  other: "その他",
};

interface EvaluationReport {
  id: string;
  categories: string[];
  detail: string;
  topicId: string | null;
  theme: string | null;
  difficulty: string | null;
  totalScore: number | null;
  userOpinion: string | null;
  analyticsEvaluationId: string | null;
  createdAt: { _seconds: number };
  status: "pending" | "read" | "resolved";
  platform: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  appVersion: string | null;
}

const EvaluationReportList: React.FC = () => {
  const [reports, setReports] = useState<EvaluationReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<EvaluationReport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EvaluationReport | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // 「評価の詳細を開く」で表示する analyticsEvaluations のドキュメントID（null のとき非表示）
  const [evaluationDetailId, setEvaluationDetailId] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "customerEvaluationReports"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const reportList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const createdAt = data.createdAt as Timestamp | null;
        return {
          id: docSnap.id,
          categories: Array.isArray(data.categories) ? data.categories : [],
          detail: data.detail || "",
          topicId: data.topicId || null,
          theme: data.theme || null,
          difficulty: data.difficulty || null,
          totalScore: typeof data.totalScore === "number" ? data.totalScore : null,
          userOpinion: data.userOpinion || null,
          analyticsEvaluationId: data.analyticsEvaluationId || null,
          status: data.status || "pending",
          createdAt: createdAt
            ? { _seconds: createdAt.seconds }
            : { _seconds: Math.floor(Date.now() / 1000) },
          platform: data.platform || null,
          osVersion: data.osVersion || null,
          deviceModel: data.deviceModel || null,
          appVersion: data.appVersion || null,
        } as EvaluationReport;
      });
      setReports(reportList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleOpenDialog = (report: EvaluationReport) => {
    setSelectedReport(report);
    setDialogOpen(true);

    if (report.status === "pending") {
      updateReportStatus(report.id, "read");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReport(null);
  };

  const updateReportStatus = async (reportId: string, status: "read" | "resolved") => {
    try {
      const docRef = doc(db, "customerEvaluationReports", reportId);
      await updateDoc(docRef, {
        status,
        updatedAt: serverTimestamp(),
      });

      setReports((prev) =>
        prev.map((report) => (report.id === reportId ? { ...report, status } : report))
      );

      if (selectedReport?.id === reportId) {
        setSelectedReport({ ...selectedReport, status });
      }
    } catch (err) {
      console.error("Error updating evaluation report status:", err);
      alert("ステータスの更新に失敗しました");
    }
  };

  const handleMarkAsResolved = async () => {
    if (selectedReport) {
      await updateReportStatus(selectedReport.id, "resolved");
      handleCloseDialog();
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, "customerEvaluationReports", deleteTarget.id));
      setReports((prev) => prev.filter((report) => report.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Error deleting evaluation report:", err);
      alert("削除に失敗しました");
    } finally {
      setIsDeleting(false);
    }
  };

  const getCategoryLabel = (category: string) => CATEGORY_LABELS[category] || category;

  const getDifficultyLabel = (difficulty: string | null) => {
    if (!difficulty) return "-";
    return `難易度${difficulty.replace("difficulty_", "")}`;
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "未読";
      case "read":
        return "既読";
      case "resolved":
        return "対応済み";
      default:
        return status;
    }
  };

  const getStatusColor = (
    status: string
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case "pending":
        return "error";
      case "read":
        return "warning";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            評価結果フィードバック
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            評価結果画面からユーザーが送信したフィードバックの一覧です
          </Typography>

          <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ステータス</TableCell>
                  <TableCell>投稿日時</TableCell>
                  <TableCell>おかしい項目</TableCell>
                  <TableCell>スコア</TableCell>
                  <TableCell>お題</TableCell>
                  <TableCell>詳細（プレビュー）</TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      フィードバックはありません
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(report.status)}
                          color={getStatusColor(report.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(report.createdAt._seconds * 1000).toLocaleString("ja-JP")}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {report.categories.map((category) => (
                            <Chip
                              key={category}
                              label={getCategoryLabel(category)}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {report.totalScore !== null ? `${report.totalScore}点` : "-"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {report.theme || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {report.detail
                          ? report.detail.length > 30
                            ? `${report.detail.substring(0, 30)}...`
                            : report.detail
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog(report)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setDeleteTarget(report)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 詳細ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          評価結果フィードバック詳細
          {selectedReport && (
            <Chip
              label={getStatusLabel(selectedReport.status)}
              color={getStatusColor(selectedReport.status)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                投稿日時
              </Typography>
              <Typography variant="body1" paragraph>
                {new Date(selectedReport.createdAt._seconds * 1000).toLocaleString("ja-JP")}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                おかしいと報告された項目
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedReport.categories.map(getCategoryLabel).join("、")}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                評価情報
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Typography variant="body1">
                  総合スコア:{" "}
                  {selectedReport.totalScore !== null ? `${selectedReport.totalScore}点` : "不明"} /{" "}
                  {getDifficultyLabel(selectedReport.difficulty)}
                </Typography>
                {selectedReport.analyticsEvaluationId && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNew />}
                    onClick={() => setEvaluationDetailId(selectedReport.analyticsEvaluationId)}
                  >
                    評価の詳細を開く
                  </Button>
                )}
              </Box>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                お題
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedReport.theme || "不明"}
                {selectedReport.topicId ? `（topicId: ${selectedReport.topicId}）` : ""}
              </Typography>

              {selectedReport.userOpinion && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    ユーザーの回答
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: "grey.50", mb: 2 }}>
                    <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedReport.userOpinion}
                    </Typography>
                  </Paper>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                詳細（自由入力）
              </Typography>
              <Paper sx={{ p: 2, bgcolor: "grey.50", mb: 2 }}>
                <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedReport.detail || "（入力なし）"}
                </Typography>
              </Paper>

              {selectedReport.platform && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    端末情報
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReport.deviceModel || "不明"} / {selectedReport.platform}{" "}
                    {selectedReport.osVersion || ""} / アプリ v{selectedReport.appVersion || "不明"}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedReport && selectedReport.status !== "resolved" && (
            <Button
              startIcon={<CheckCircle />}
              variant="contained"
              color="success"
              onClick={handleMarkAsResolved}
            >
              対応済みにする
            </Button>
          )}
          <Button onClick={handleCloseDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 評価詳細ダイアログ（analyticsEvaluations の該当ドキュメントを表示） */}
      <EvaluationDetailDialog
        open={evaluationDetailId !== null}
        evaluationId={evaluationDetailId}
        onClose={() => setEvaluationDetailId(null)}
      />

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>評価結果フィードバックを削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            この操作は取り消せません。イタズラなど不要な投稿を完全に削除します。
          </Typography>
          {deleteTarget && (
            <Paper sx={{ p: 2, bgcolor: "grey.50", mt: 2 }}>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {deleteTarget.categories.map(getCategoryLabel).join("、")}
                {deleteTarget.detail
                  ? `\n${
                      deleteTarget.detail.length > 200
                        ? `${deleteTarget.detail.substring(0, 200)}...`
                        : deleteTarget.detail
                    }`
                  : ""}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationReportList;
